/**
 * Taking money.
 *
 * The one rule that outranks everything else in this application:
 *
 *   **A terminal approval is money already taken.** The card is charged on the
 *   counter by hardware that does not know the internet exists. Between the
 *   approval and the server acknowledging the order, the only record of that
 *   money is the outbox — so the claim is written and *awaited* before the
 *   success screen, before the receipt, and before the POST.
 *
 * Everything else here follows from that. `docs/offline.md` is the contract.
 */
import { lineOrderType, roundUpToTugrik, toWholeTugrik, totalsOf } from '@mgc/domain'
import type { Money, PricedLine } from '@mgc/domain'
import type { CreateOrderInput, OrderView } from '@mgc/protocol'
import { isServerFault, reasonOf, refusalOf, trpc } from '@/api/client'
import {
  claim,
  acknowledge,
  deserialiseOrder,
  needsAPerson,
  noteAttempt,
  outstanding,
  reject,
  serialiseOrder
} from '@/outbox/outbox'
import { mayHaveTakenMoney, shellLog, terminalPay, tookMoney } from '@/shell/native'
import type { Capture } from '@/store/kiosk'

export type PayMethod = 'cash' | 'card' | 'qpay'

export interface PayContext {
  readonly orderId: string
  readonly orderType: 'eat_in' | 'take_out'
  readonly lines: readonly PricedLine[]
  readonly company: { registrationNumber: string; name: string; tin?: string } | null
  /** Twelve letters and digits the customer typed, for 1C. */
  readonly coupon?: string
  readonly note?: string
  readonly placedAt: string
}

/**
 * Whether a customer's card was charged — in three values, because it has
 * three.
 *
 * This was a `boolean`, and `'maybe'` was folded onto `false`. A terminal that
 * stopped answering may well have taken the money, and saying "no money taken"
 * about it meant no outbox entry was written and the charge existed nowhere.
 */
export type MoneyTaken = 'no' | 'yes' | 'maybe'

export type PayResult =
  | {
      readonly ok: true
      readonly order: OrderView | null
      readonly offline: boolean
      /**
       * What was charged, from the kiosk's own side of the wire.
       *
       * Carried because `order` is null when the server could not be reached,
       * and that is precisely the sale whose paper has to stand on its own at a
       * counter. See `Capture`.
       */
      readonly capture: Capture
    }
  | {
      readonly ok: false
      readonly refusal: string
      readonly moneyTaken: MoneyTaken
      /**
       * What the server actually said, when it said anything — 1C's own
       * sentence, a crash body, an HTTP status. The `refusal` names a category
       * and has a translated string; this is the half that names the instance,
       * and it is shown beside the translation and written to the shell log.
       */
      readonly reason?: string
    }

/**
 * A line override only when it disagrees with the order — and never take-away
 * on an eat-in-only product, even if the cart still holds that leftover.
 */
function lineTypeOverride(line: PricedLine, cartType: PayContext['orderType']) {
  const served = lineOrderType(line.product, line.selection.orderType, cartType)
  if (served === null || served === cartType) return {}
  return { orderType: served }
}

/** The order as the server wants it, priced by the same code the server runs. */
export function toCreateInput(
  context: PayContext,
  settled: CreateOrderInput['settledPayments'] = [],
  /**
   * The method being tapped. 1C posts the sale from its reservation's payment
   * type, and this call is what reserves — see `orders.create`.
   */
  method?: PayMethod
): CreateOrderInput {
  const total = totalsOf({ lines: context.lines, payments: [] }).gross
  return {
    id: context.orderId,
    orderType: context.orderType,
    lines: context.lines.map((line) => ({
      id: line.selection.id,
      productId: line.selection.productId,
      quantity: line.selection.quantity,
      ...(line.selection.temperature === undefined ? {} : { temperature: line.selection.temperature }),
      ...(line.selection.changeCode === undefined ? {} : { changeCode: line.selection.changeCode }),
      addCodes: [...line.selection.addCodes],
      ...lineTypeOverride(line, context.orderType),
      ...(line.selection.note === undefined ? {} : { note: line.selection.note })
    })),
    claimed: { total, lines: context.lines.map((l) => ({ id: l.selection.id, total: l.total })) },
    company: context.company,
    ...(context.coupon === undefined ? {} : { coupon: context.coupon }),
    ...(context.note === undefined ? {} : { note: context.note }),
    placedAt: context.placedAt,
    ...(method === undefined ? {} : { method }),
    settledPayments: settled
  }
}

/**
 * Get a captured payment onto the server, whether or not the order is new.
 *
 * `orders.create` is the idempotency key for an *order*, and it early-returns
 * the existing order without looking at `settledPayments`. That is right for a
 * replay of the same single payment and silently wrong for a second one: the
 * call succeeds, returns an order that does not contain the payment, and the
 * caller acknowledges a charge the server never recorded. For a card that is a
 * real approval erased from the only place it was written.
 *
 * So the create is the attempt and **the returned view is the proof**. If the
 * payment is not in it, the order already existed and the payment has to go on
 * separately — `addPayment` is `onConflictDoNothing` and `setPaymentState` is
 * written to tolerate being called twice, so this is safe to repeat and safe to
 * interleave with a replay of the same entry.
 *
 * Throws exactly what the tRPC calls throw, so every caller's existing
 * refusal-versus-offline handling is unchanged.
 */
async function settleOnServer(
  input: CreateOrderInput,
  payment: {
    readonly id: string
    readonly orderId: string
    readonly method: PayMethod
    readonly amount: Money
    readonly reference: string | null
    readonly detail: Record<string, string> | null
  }
): Promise<OrderView> {
  const created = await trpc.orders.create.mutate(input)
  if (created.payments.some((p) => p.id === payment.id)) return created

  await trpc.orders.addPayment.mutate({
    orderId: payment.orderId,
    paymentId: payment.id,
    method: payment.method,
    amount: payment.amount,
    ...(payment.reference === null ? {} : { reference: payment.reference }),
    ...(payment.detail === null ? {} : { detail: payment.detail })
  })
  const settled = await trpc.orders.setPaymentState.mutate({
    orderId: payment.orderId,
    paymentId: payment.id,
    state: 'captured',
    ...(payment.reference === null ? {} : { reference: payment.reference })
  })
  // `create` returned this order a moment ago, so a null here is the order
  // having been deleted underneath us. Throwing keeps the claim on disk, which
  // is the safe direction: the alternative is acknowledging against nothing.
  if (settled === null) throw new Error(`order ${payment.orderId} vanished while settling ${payment.id}`)
  return settled
}

/**
 * How long the pre-flight waits for the server before the answer is «no».
 *
 * Bounded on purpose: this runs with a customer's finger still on the button
 * and before any money moves, so the cost of being wrong is a refusal they can
 * act on, not a charge.
 *
 * Six seconds rather than the two this was, because the pre-flight stopped
 * being a liveness ping and became a 1C reservation — which for a shop on a
 * connector is a queued job, a long poll and an answer carried back. Two
 * seconds would refuse sales that were about to succeed. It is still shorter
 * than a customer will stand there wondering, and shorter than the terminal
 * interaction that follows it.
 */
const RESERVE_BUDGET_MS = 6_000

/**
 * Register the sale and have 1C hold the stock — **before the card is charged**.
 *
 * This was `serverReachable()`, a `branch.status` query whose answer nobody
 * read. It asked the right question at the right moment and it asked the
 * smallest version of it: *is anything there?* A server that answers and a shop
 * that can actually make the drink are different facts, and the gap between
 * them was paid for at the settle, after the terminal had approved — where
 * «no stock», «the branch is closed», «no cash shift is open» and «1C has never
 * heard of this SKU» all arrive too late to do anything but file a row in the
 * console.
 *
 * So the pre-flight is now the real thing: `orders.create` with no payments,
 * which the server answers by asking 1C for a `paid: 0` reservation and
 * refusing the sale if 1C will not give one. It proves the server, the
 * catalogue, the shift and the stock in one round trip, and it leaves the order
 * on the server — so `settleOnServer` below finds it already there after the
 * terminal and goes through `addPayment`, which it was already written to do.
 *
 * **A refusal here costs nothing**, which is the entire reason it is here and
 * not one line further down. Past this point a charge may exist and the outbox
 * rules.
 *
 * The budget is unchanged in spirit and now buys more: a reservation takes a
 * 1C round trip through the shop's own connector, so it is slower than a status
 * ping. It is still bounded, because a customer with a finger on the button is
 * owed an answer either way.
 */
type Preflight =
  | { readonly ok: true }
  | { readonly ok: false; readonly refusal: string; readonly reason: string | null }

/**
 * Three ways to fail, kept apart, because they send a customer three places.
 *
 * A refusal the server **authored** — `onec-refused`, `branch-closed`,
 * `price-mismatch` — names something true about this shop or this basket. A
 * server that **answered with a fault** is up and broken. A server that did not
 * answer at all is the network. All three used to collapse into «offline»,
 * which tells a customer the wrong story about two of them and leaves nothing
 * in the log to act on.
 */
function preflightFailure(error: unknown): Preflight {
  const refusal = refusalOf(error)
  if (refusal !== null) return { ok: false, refusal, reason: reasonOf(error) }
  // A 500, a validation failure, a body tRPC could not parse — HTML from a
  // proxy, or 1C's own crash text carried through. The server spoke; it just
  // did not say anything this kiosk was built to expect.
  if (isServerFault(error)) return { ok: false, refusal: 'server-error', reason: reasonOf(error) }
  return { ok: false, refusal: 'server-unreachable', reason: reasonOf(error) }
}

async function reserveOnServer(input: CreateOrderInput): Promise<Preflight> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const outcome = await Promise.race([
    trpc.orders.create.mutate(input).then((): Preflight => ({ ok: true }), preflightFailure),
    new Promise<Preflight>((resolve) => {
      timer = setTimeout(
        () =>
          resolve({
            ok: false,
            refusal: 'server-unreachable',
            reason: `no answer within ${RESERVE_BUDGET_MS}ms`
          }),
        RESERVE_BUDGET_MS
      )
    })
  ])
  if (timer !== undefined) clearTimeout(timer)
  return outcome
}

/**
 * Charge a card.
 *
 * The order of the four steps is the whole point and is not negotiable:
 * approve, **record**, then attempt the server. A crash anywhere after step two
 * leaves the claim on disk and the recovery banner finds it on next boot.
 *
 * Step two now runs on `maybe` as well as on `yes`, which is the correction to
 * the worst defect in the tree: the shell answered a timeout with
 * `approved: false`, this function read that as "no money taken", and a sale the
 * terminal went on to approve was recorded nowhere at all.
 */
export async function payByCard(context: PayContext, terminalId: string, paymentId: string): Promise<PayResult> {
  // What the terminal charges: the total rounded up to a whole tugrik. See `roundUpToTugrik`.
  const amount = roundUpToTugrik(totalsOf({ lines: context.lines, payments: [] }).gross)
  const input = toCreateInput(context, [], 'card')

  /*
   * 0. Register the sale, and have 1C hold the stock.
   *
   * The one step that must happen **before** the terminal, because it is the
   * last moment at which refusing is free. A card charged with the server down
   * is money taken for a coffee nobody in the shop can see: no order number, no
   * row on the attendant screen, no 1C document — the sale exists on one slip
   * and in one IndexedDB, and a person has to reconstruct it at the counter.
   * `docs/offline.md` describes that recovery and it is a recovery, not a
   * feature.
   *
   * It used to ask only whether the server answered. Now it creates the order
   * unpaid, which makes the server ask 1C for a reservation — so a shop that is
   * out of milk, closed, or has no cash shift open refuses **here**, for free,
   * instead of at the filing after the customer has paid. That is the half that
   * lets a failed filing stop retrying; see `services/fiscal.ts:MAX_ATTEMPTS`.
   *
   * So the kiosk stops selling instead. That is a real cost — a shop with a
   * dead uplink takes no self-service card payments — and it is the smaller
   * one: the counter can still sell, and the customer is told before they have
   * paid rather than afterwards.
   *
   * Everything below this line is unchanged. Once the terminal has been asked,
   * a charge may exist, and from there the outbox rules and nothing else.
   */
  const reserved = await reserveOnServer(input)
  if (!reserved.ok) {
    // The reason as well as the code. A log saying only `onec-refused` cannot
    // be debugged; one saying «нөөц хүрэлцэхгүй: Латте» can.
    shellLog(
      'warn',
      `order ${context.orderId}: refused a card payment before the terminal — ${reserved.refusal}` +
        (reserved.reason === null ? '' : `: ${reserved.reason}`)
    )
    return {
      ok: false,
      refusal: reserved.refusal,
      moneyTaken: 'no',
      ...(reserved.reason === null ? {} : { reason: reserved.reason })
    }
  }

  // 1. The terminal. Long by design; the modal above this is cancellable.
  //
  // `mayHaveTakenMoney`, not `outcome === 'approved'`: an `unknown` means the
  // shell stopped waiting without learning whether the card was charged. Only a
  // definite decline returns here having moved no money.
  const result = await terminalPay(terminalId, toWholeTugrik(amount))
  if (!mayHaveTakenMoney(result)) {
    // The refusal is the category, never the terminal's code. The code went
    // here once, so a GLMTPOS `99` looked up `refusal.99`, found nothing and
    // drew «Алдаа гарлаа» — while the shell's own sentence, the one line that
    // said why, was dropped. The code and the sentence are the reason.
    const reason = [result.message, result.code === null ? null : `(${result.code})`]
      .filter((part) => part !== null && part.length > 0)
      .join(' ')
    shellLog('warn', `order ${context.orderId}: the terminal declined — ${reason || 'no message'}`)
    return {
      ok: false,
      refusal: 'terminal-declined',
      moneyTaken: 'no',
      ...(reason.length === 0 ? {} : { reason })
    }
  }

  const capturedAt = new Date().toISOString()
  const ambiguous = !tookMoney(result)

  // 2. Record it, and *wait*. Nothing below runs until this is on disk.
  await claim(
    {
      id: paymentId,
      orderId: context.orderId,
      method: 'card',
      amount: amount.toString(),
      reference: result.approvalCode,
      detail: result.maskedPan === null ? null : { pan: result.maskedPan },
      capturedAt,
      order: serialiseOrder(input)
    },
    ambiguous
  )

  // 2a. An ambiguous charge is recorded but **not posted**.
  //
  // The symmetry is the argument. Losing a charge we did take is the failure
  // this whole file exists to prevent; posting a capture we may *not* hold is
  // the same failure with its sign flipped — it marks the order paid, files an
  // e-Barimt receipt against money nobody received, and moves stock in 1C. And
  // e-Barimt does not dedupe: undoing one means finding its ДДТД and issuing a
  // delete. So the claim waits on the banner until somebody reads the
  // terminal's own batch and says which it was.
  if (ambiguous) {
    shellLog(
      'error',
      `order ${context.orderId}: the terminal did not say whether it charged ${amount}; ` +
        `claim ${paymentId} is held unresolved`
    )
    return { ok: false, refusal: 'terminal-unresolved', moneyTaken: 'maybe' }
  }

  const capture: Capture = { method: 'card', amount, reference: result.approvalCode }

  // 3. Tell the server, with the approval attached.
  //
  // Through `settleOnServer` rather than `orders.create` alone, because this
  // may be the second payment id on one order — a QPay code the customer
  // abandoned and renewed, then paid by card — and a bare create would return
  // the existing order having recorded nothing, leaving this function to
  // acknowledge a charge the server does not have.
  try {
    const order = await settleOnServer(
      toCreateInput(
        context,
        [
          {
            id: paymentId,
            method: 'card',
            amount,
            ...(result.approvalCode === null ? {} : { reference: result.approvalCode }),
            ...(result.maskedPan === null ? {} : { detail: { pan: result.maskedPan } }),
            capturedAt
          }
        ],
        'card'
      ),
      {
        id: paymentId,
        orderId: context.orderId,
        method: 'card',
        amount,
        reference: result.approvalCode,
        detail: result.maskedPan === null ? null : { pan: result.maskedPan }
      }
    )
    await acknowledge(paymentId)
    return { ok: true, order, offline: false, capture }
  } catch (error) {
    const refusal = refusalOf(error)
    if (refusal !== null) {
      // The server refused an order the customer has already paid for. The
      // claim stays; a person has to deal with it.
      await reject(paymentId, refusal)
      shellLog('error', `order ${context.orderId} refused after a card was charged: ${refusal}`)
      return { ok: false, refusal, moneyTaken: 'yes' }
    }
    // Offline. The money is taken, the receipt prints, and the outbox replays.
    await noteAttempt(paymentId)
    shellLog('warn', `order ${context.orderId} could not reach the server; held in the outbox`)
    return { ok: true, order: null, offline: true, capture }
  }
}

/**
 * Cash.
 *
 * Needs the server, and that is correct: nothing has been charged, so a failure
 * is a message rather than a liability. An attendant taking cash with no uplink
 * is a decision somebody makes, not one this function makes for them.
 */
export async function payByCash(context: PayContext, paymentId: string, tendered: Money): Promise<PayResult> {
  const amount = totalsOf({ lines: context.lines, payments: [] }).gross
  try {
    await trpc.orders.create.mutate(toCreateInput(context, [], 'cash'))
    await trpc.orders.addPayment.mutate({
      orderId: context.orderId,
      paymentId,
      method: 'cash',
      amount: tendered > amount ? tendered : amount
    })
    const order = await trpc.orders.setPaymentState.mutate({
      orderId: context.orderId,
      paymentId,
      state: 'captured'
    })
    return { ok: true, order, offline: false, capture: { method: 'cash', amount, reference: null } }
  } catch (error) {
    // Cash is handed over *before* this runs, so a failure here is not
    // "no money taken" in the till's sense — but nothing was captured
    // server-side and every call below is idempotent, so a retry is safe and
    // the attendant is standing right there.
    // With the server's own words, for the same reason `startQPay` carries them.
    const reason = reasonOf(error)
    return {
      ok: false,
      refusal: refusalOf(error) ?? 'offline',
      moneyTaken: 'no',
      ...(reason === null ? {} : { reason })
    }
  }
}

/**
 * QPay: create the order, create the invoice, and wait to be *told*.
 *
 * The old client polled. The settle arrives over the socket now, and
 * `qpayCheck` is the fallback for a kiosk that was offline while the customer
 * paid.
 */
export async function startQPay(
  context: PayContext,
  paymentId: string
): Promise<{ ok: true; qrText: string; invoiceId: string } | { ok: false; refusal: string; reason: string | null }> {
  // What the invoice asks for: the total rounded up to a whole tugrik. See `roundUpToTugrik`.
  const amount = roundUpToTugrik(totalsOf({ lines: context.lines, payments: [] }).gross)
  try {
    await trpc.orders.create.mutate(toCreateInput(context, [], 'qpay'))
    await trpc.orders.addPayment.mutate({ orderId: context.orderId, paymentId, method: 'qpay', amount })
    const invoice = await trpc.payments.qpayInvoice.mutate({ orderId: context.orderId, paymentId })
    return { ok: true, qrText: invoice.qrText, invoiceId: invoice.invoiceId }
  } catch (error) {
    // The reason as well as the code, exactly as the card path carries it: a
    // refused QPay drew only «кассанд хандана уу» while the same refusal on a
    // card drew 1C's own sentence — «нөөц хүрэлцэхгүй: Латте» — under it.
    return { ok: false, refusal: refusalOf(error) ?? 'offline', reason: reasonOf(error) }
  }
}

export async function cancelQPay(paymentId: string): Promise<void> {
  await trpc.payments.qpayCancel.mutate({ paymentId }).catch(() => {})
}

/**
 * Replay whatever the server has not acknowledged.
 *
 * Run on boot and on every reconnect. Idempotent at both ends: the kiosk minted
 * the order id and the payment id, so a POST that already landed comes back as
 * the order that exists.
 */
export async function replayOutbox(): Promise<{ sent: number; rejected: number; remaining: number }> {
  const entries = await outstanding()
  let sent = 0
  let rejected = 0

  // Ask first what the server already has.
  //
  // A kiosk that posted an order and lost the response — or was restarted
  // between the POST and the acknowledgement — is holding a claim the server
  // accepted. Replaying it is harmless (the id is the idempotency key) but
  // asking is cheaper than re-sending a basket, and it is the only way an
  // entry the server *rejected* while this kiosk was away reaches the banner
  // as a rejection rather than being retried for ever.
  if (entries.length > 0) {
    try {
      const known = await trpc.orders.outboxState.query({ ids: entries.map((e) => e.id).slice(0, 100) })
      for (const row of known) {
        if (row.state === 'accepted') await acknowledge(row.id)
        if (row.state === 'rejected') await reject(row.id, row.rejection ?? 'refused by the server')
      }
    } catch {
      // Offline. Fall through and try the replay, which is what would have
      // happened without this step.
    }
  }

  for (const entry of await outstanding()) {
    // Both of these need a person, for opposite reasons: a rejection is money
    // taken the server will not accept, and an `unresolved` is money we are not
    // sure we hold. Retrying either on a timer would be this loop deciding
    // something it has no way to know.
    if (needsAPerson(entry)) continue
    try {
      await noteAttempt(entry.id)
      /*
       * The same proof the live path takes.
       *
       * A replay used to be `create` then `acknowledge`, and `create` returns
       * an existing order without recording a payment it does not already
       * hold. For a second payment on one order that acknowledged — and so
       * deleted the only record of — a charge the server never received. The
       * helper asks the returned order whether the payment is really there and
       * attaches it if not.
       */
      await settleOnServer(deserialiseOrder(entry.order, entry), {
        id: entry.id,
        orderId: entry.orderId,
        method: entry.method,
        amount: BigInt(entry.amount) as Money,
        reference: entry.reference,
        detail: entry.detail === null ? null : entry.detail
      })
      await acknowledge(entry.id)
      sent += 1
    } catch (error) {
      const refusal = refusalOf(error)
      if (refusal !== null) {
        await reject(entry.id, refusal)
        rejected += 1
      }
      // Still offline: leave it exactly as it is and try again next time.
    }
  }

  return { sent, rejected, remaining: (await outstanding()).length }
}

/**
 * Tell the server nobody is going to pay for this, so 1C gets its stock back.
 *
 * A reservation is taken before the terminal is touched, and a customer who
 * walks away from the payment screen leaves it held. `releaseExpired` collects
 * it after fifteen minutes; on a self-service panel, where walking away is the
 * ordinary outcome and not the exception, fifteen minutes of held stock per
 * abandoned basket is a shelf that reads as empty.
 *
 * **Nothing waits for this and nothing reads its answer.** There is no customer
 * left to tell and no screen to put a refusal on: the server refuses it outright
 * the moment any money is against the order, so the worst case is a reservation
 * that expires the way it always did. It is a `void` return for that reason.
 */
export function abandonOrder(orderId: string): void {
  void trpc.orders.abandon
    .mutate({ orderId })
    .catch((error: unknown) =>
      shellLog('warn', `order ${orderId}: could not release the reservation — ${refusalOf(error) ?? 'offline'}`)
    )
}
